import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/pro-light-svg-icons';
import { RESOURCE_STATUS } from '../../common/constants';
import { formatListDate } from '../../common/helpers';
import { TitleIcon } from '../TitleIcon';
import { getStatusClass } from '../helpers/resourceStatus';

/**
 * One resource in the phone list: its title and date, with a menu button that
 * reveals Edit and Delete on a row of their own underneath.
 *
 * Props:
 *   id           (string)   - ID prefix for generated element IDs.
 *   resource     (object)   - The resource to show.
 *   hasEditor    (bool)     - Whether this kind of resource can be edited.
 *   areActionsOpen (bool)   - Whether this row's actions are revealed.
 *   onToggleActions (function) - Show or hide this row's actions.
 *   onOpen       (function) - Open the resource itself.
 *   onEdit       (function) - Open the resource's editor.
 *   onDelete     (function) - Ask to delete the resource.
 */

export const ResourceRow = ({
  id,
  resource,
  hasEditor,
  areActionsOpen,
  onToggleActions,
  onOpen,
  onEdit,
  onDelete,
}) => {
  const rowId = `${id}__row_${resource.id}`;
  const isReady = resource.status === RESOURCE_STATUS.COMPLETED;
  // Matches the table: a spinner while generating, a warning if it failed.
  const statusClass = getStatusClass(resource.status);

  return (
    <Box className="resourceRow" id={rowId}>
      <Box className="resourceRowMain">
        <Box
          className="resourceRowText"
          role="button"
          tabIndex={0}
          onClick={onOpen}
          onKeyDown={(e) => e.key === 'Enter' && onOpen()}
        >
          <Box className="resourceRowTitleLine">
            <TitleIcon status={resource.status} />
            {/*
              Cut by the row rather than by a character count, as in the table:
              the row is as wide as the screen makes it, so CSS is what knows
              where the name has to stop. The whole name stays in the DOM for a
              screen reader, and the row itself is the tab stop.
            */}
            <span className={`resourceRowTitle ${statusClass}`} title={resource.title}>
              {resource.title}
            </span>
          </Box>
          <Typography className={`resourceRowDate ${statusClass}`}>
            {formatListDate(resource.createdAt)}
          </Typography>
        </Box>

        <IconButton
          id={`${rowId}__iconButton_actions`}
          className="resourceRowMenu"
          onClick={onToggleActions}
          aria-label={`Actions for ${resource.title}`}
          aria-expanded={areActionsOpen}
        >
          <FontAwesomeIcon icon={faEllipsis} />
        </IconButton>
      </Box>

      {areActionsOpen && (
        <Box className="resourceRowActions">
          {hasEditor && (
            <Button
              variant="contained"
              className="mss__button rowActionButton"
              id={`${rowId}__button_edit`}
              disabled={!isReady}
              onClick={onEdit}
            >
              Edit
            </Button>
          )}
          <Button
            variant="contained"
            className="mss__button primary rowActionButton"
            id={`${rowId}__button_delete`}
            onClick={onDelete}
          >
            Delete
          </Button>
        </Box>
      )}
    </Box>
  );
};
